import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGdprAdminAuth } from '../context/GdprAdminAuthContext';
import {
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

/**
 * GDPR Admin Login Page
 *
 * Secure login page specifically for GDPR Admin Console access.
 * - Only accepts the username 'gdpradmin2026'
 * - Password is a GUID stored in Azure Key Vault
 * - Session times out after 15 minutes of inactivity
 */
const GdprAdminLoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  const { login, isAuthenticated, isLoading, error, clearError } = useGdprAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to admin page if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/gdpradm';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Clear errors on input change
  useEffect(() => {
    if (localError || error) {
      setLocalError('');
      clearError();
    }
  }, [username, password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    // Basic validation
    if (!username.trim()) {
      setLocalError('Username is required');
      return;
    }
    if (!password.trim()) {
      setLocalError('Password is required');
      return;
    }

    const success = await login(username.trim(), password);
    if (success) {
      const from = location.state?.from?.pathname || '/gdpradm';
      navigate(from, { replace: true });
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E3056] via-[#1a4a7a] to-[#0E3056] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#FFC000] rounded-full mb-4 shadow-lg">
            <ShieldCheckIcon className="h-10 w-10 text-[#0E3056]" />
          </div>
          <h1 className="text-3xl font-bold text-white">GDPR Admin Console</h1>
          <p className="text-gray-300 mt-2">Secure Authentication Required</p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8">
          {/* Security Notice */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 mb-6 rounded-r-lg">
            <div className="flex items-start">
              <LockClosedIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3" />
              <div>
                <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                  Restricted Access
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  This area is restricted to authorized GDPR administrators only.
                  Sessions automatically expire after 15 minutes of inactivity.
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {displayError && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                <p className="text-sm text-red-700 dark:text-red-300">{displayError}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username Field */}
            <div>
              <label
                htmlFor="gdpr-username"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Administrator Username
              </label>
              <input
                id="gdpr-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm
                         focus:ring-2 focus:ring-[#FFC000] focus:border-[#FFC000]
                         dark:bg-gray-700 dark:text-white transition-colors"
                placeholder="Enter administrator username"
                autoComplete="username"
                disabled={isLoading}
                autoFocus
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="gdpr-password"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="gdpr-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm
                           focus:ring-2 focus:ring-[#FFC000] focus:border-[#FFC000]
                           dark:bg-gray-700 dark:text-white transition-colors"
                  placeholder="Enter password"
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center py-3 px-4
                       bg-[#0E3056] hover:bg-[#1a4a7a] text-white font-semibold rounded-lg
                       shadow-md hover:shadow-lg transition-all duration-200
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  Authenticating...
                </>
              ) : (
                <>
                  <ShieldCheckIcon className="h-5 w-5 mr-2" />
                  Authenticate
                </>
              )}
            </button>
          </form>

          {/* Footer Info */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              For credential access, please contact your system administrator
              or retrieve from Azure Key Vault.
            </p>
          </div>
        </div>

        {/* Back Link */}
        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-sm text-gray-300 hover:text-white transition-colors"
          >
            &larr; Back to Main Site
          </a>
        </div>
      </div>
    </div>
  );
};

export default GdprAdminLoginPage;
